import { IBuilder, RowType } from '../../../../redux/slices/oldBuilder';
import { IExpressionInput, MeasureType } from '../../../apollo/graphqlSchema';
import {
  prepareCompoundMeasureInput,
  prepareRollingControlInput,
  prepareSimpleMeasureInput,
  prepareSimpleMeasurePropertiesInput,
} from './utils';
import { prepareMeasureFilterInput } from './prepareMeasureFilterInput';

/**
 * A function will prepare measure and measureProps for Query execution.
 *
 * By design, we can have:
 * 1). Simple measure that can be: basic or named.
 * 2). Compound measure: list of simple measures that can be basic or named;
 */

type IPrepareQueryExecuteMeasureInputArgs = Pick<
  IBuilder,
  RowType.measures | 'resultFilter' | 'compoundMeasureId' | 'measureFormula'
>;

export const prepareQueryExecuteMeasureInput = (
  args: IPrepareQueryExecuteMeasureInputArgs,
  schemaId: string,
): Pick<IExpressionInput, 'measure' | 'measureProperties'> => {
  const { measures, resultFilter, compoundMeasureId, measureFormula } = args;

  const type = measures.length > 1 ? MeasureType.Compound : MeasureType.Simple;

  if (type === MeasureType.Compound) {
    return {
      measure: {
        compound: prepareCompoundMeasureInput({
          measures,
          versionId: compoundMeasureId,
          measureFormula,
          schemaId,
        }),
        rollingControl: null,
        simple: null,
        type,
        format: null,
      },
      measureProperties: {
        compound: {
          measures: measures.map((measure) =>
            prepareSimpleMeasurePropertiesInput(measure, schemaId),
          ),
          resultFilter: prepareMeasureFilterInput(resultFilter),
        },
        simple: null,
        type,
      },
    };
  }

  const simpleMeasure = measures[0];

  return {
    measure: {
      compound: null,
      rollingControl: prepareRollingControlInput(
        simpleMeasure?.measureRollingControl,
      ),
      simple: prepareSimpleMeasureInput(
        {
          row: simpleMeasure,
          rowIndex: 0,
          measureFormula,
        },
        schemaId,
      ),
      type,
      format: null,
    },
    measureProperties: {
      compound: null,
      simple: prepareSimpleMeasurePropertiesInput(simpleMeasure, schemaId),
      type,
    },
  };
};
